import { useState, useEffect, useCallback, useRef } from "react";
import FleetMessagePrompt from "./FleetMessagePrompt";
import {
  serviceGetOrganizationDetails,
  serviceMessageGetAll,
  serviceMessageGetVehicle,
} from "../../api/serviceFleetMaster";
import "./FleetMessageList.css";

// ── Config ────────────────────────────────────────────────────────────────────
const STATUS_META = {
  sent: { label: "Sent", icon: "bi-check", color: "#94a3b8" },
  delivered: { label: "Delivered", icon: "bi-check-all", color: "#94a3b8" },
  accept: { label: "Accepted", icon: "bi-check-all", color: "#10b981" },
  reject: { label: "Rejected", icon: "bi-x-circle-fill", color: "#ef4444" },
  dismiss: {
    label: "Dismissed",
    icon: "bi-dash-circle-fill",
    color: "#f59e0b",
  },
};

const TYPE_META = {
  alert: { icon: "bi-exclamation-triangle-fill", color: "#ef4444" },
  command: { icon: "bi-terminal-fill", color: "#3b82f6" },
  request: { icon: "bi-question-circle-fill", color: "#f59e0b" },
  info: { icon: "bi-info-circle-fill", color: "#10b981" },
};

const PAGE_SIZE = 5;
const POLL_MS = 10_000; // single interval used everywhere
const FLASH_MS = 4_000;
const DRIVER_STATUSES = new Set(["accept", "reject", "dismiss"]);

// ── Helpers ───────────────────────────────────────────────────────────────────
function timeAgo(iso) {
  if (!iso) return "";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const days = Math.floor(diff / 86400);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

function getVehicleCode(vehicleId, fleetList = []) {
  const fleet = fleetList.find(
    (f) => String(f.vehicleId) === String(vehicleId),
  );
  return fleet?.vehicleCode ?? vehicleId;
}

// ── groupIntoConversations — remove pendingCount since we use statusChanges now
function groupIntoConversations(messages, fleetList = []) {
  const map = new Map();

  for (const msg of messages) {
    const vid = msg.vehicleId ?? "";
    if (!map.has(vid)) {
      map.set(vid, {
        vehicleId: vid,
        vehicleCode: getVehicleCode(vid, fleetList),
        lastMessage: msg,
      });
    }
  }

  return Array.from(map.values()).sort((a, b) => {
    const ta = new Date(a.lastMessage?.createdAt ?? 0);
    const tb = new Date(b.lastMessage?.createdAt ?? 0);
    return tb - ta;
  });
}

function normaliseMsg(msg) {
  return {
    _id: msg._id?.$oid ?? msg._id ?? "",
    vehicleId: msg.vehicleId?.$oid ?? msg.vehicleId ?? "",
    vehicleCode: msg.vehicleCode ?? "",
    messageType: msg.messageType ?? "info",
    messageTitle: msg.messageTitle ?? "",
    messageDetails: msg.messageDetails ?? "",
    messageStatus: msg.messageStatus ?? "sent", // fix: was "null" string
    messageCode: msg.messageCode ?? "",
    createdAt: msg.createdAt?.$date ?? msg.createdAt ?? null,
    updatedAt: msg.updatedAt?.$date ?? msg.updatedAt ?? null,
  };
}

// ── Conversation list item ────────────────────────────────────────────────────
// ── ConversationItem — remove pendingCount badge entirely
const ConversationItem = ({ convo, isActive, onClick, statusChange }) => {
  const last = convo.lastMessage;
  const statusMeta = last ? STATUS_META[last.messageStatus] : null;
  const typeMeta = last ? TYPE_META[last.messageType] : null;

  const changeLabel = {
    accept: { text: "Accepted", cls: "mdl-change-pill--accept" },
    reject: { text: "Rejected", cls: "mdl-change-pill--reject" },
    dismiss: { text: "Dismissed", cls: "mdl-change-pill--dismiss" },
  }[statusChange];

  return (
    <button
      className={`mdl-convo-item ${isActive ? "mdl-convo-item--active" : ""}`}
      onClick={() => onClick(convo)}
    >
      <div
        className={`mdl-avatar ${statusChange ? "mdl-convo-item--changed" : ""}`}
      >
        <i className="bi bi-truck" />
        {/* badge removed — status changes shown via changeLabel pill instead */}
      </div>

      <div className="mdl-convo-body">
        <div className="mdl-convo-details">
          <span className="mdl-convo-rego">
            {convo.vehicleCode || convo.vehicleId}
          </span>
          {last && (
            <div className="mdl-convo-preview">
              <span className="mdl-preview-title">{last.messageTitle}</span>
              {statusMeta && (
                <i
                  className={`bi ${statusMeta.icon} mdl-preview-status`}
                  style={{ color: statusMeta.color }}
                />
              )}
            </div>
          )}
        </div>

        <div className="mdl-convo-time-status">
          {last && (
            <span className="mdl-convo-time">
              {timeAgo(last.createdAt?.$date ?? last.createdAt)}
            </span>
          )}
          {changeLabel ? (
            <span className={`mdl-change-pill ${changeLabel.cls}`}>
              {changeLabel.text}
            </span>
          ) : (
            typeMeta && (
              <i
                className={`bi ${typeMeta.icon} mdl-preview-type-icon`}
                style={{ color: typeMeta.color }}
              />
            )
          )}
        </div>
      </div>
    </button>
  );
};

// ── Message bubble ────────────────────────────────────────────────────────────
const MessageBubble = ({ msg, isNew }) => {
  const typeMeta = TYPE_META[msg.messageType] ?? TYPE_META.info;
  const statusMeta = STATUS_META[msg.messageStatus] ?? STATUS_META.sent;

  return (
    <div
      className={`mdl-bubble mdl-bubble--${msg.messageType ?? "info"}${isNew ? " mdl-bubble--new" : ""}`}
    >
      <div className="mdl-bubble-header">
        <i className={`bi ${typeMeta.icon} mdl-bubble-type-icon`} />
        <span className="mdl-bubble-type">
          {msg.messageType?.toUpperCase()}
        </span>
        <span className="mdl-bubble-time">{timeAgo(msg.createdAt)}</span>
      </div>
      <div className="mdl-bubble-title">{msg.messageTitle}</div>
      <div className="mdl-bubble-body">{msg.messageDetails}</div>
      <div className="mdl-bubble-footer">
        <span className="mdl-bubble-code">#{msg.messageCode}</span>
        <span
          className={`mdl-status-pill mdl-status-pill--${msg.messageStatus}`}
        >
          <i className={`bi ${statusMeta.icon}`} />
          {statusMeta.label}
        </span>
      </div>
    </div>
  );
};

// ── Thread view — no polling, receives messages from parent ──────────────────
const ThreadView = ({
  convo,
  organizationId,
  messages,
  onBack,
  onCompose,
  onLoadMore,
  hasMore,
  loadingMore,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newestId, setNewestId] = useState(null);

  const bodyRef = useRef(null);
  const bottomRef = useRef(null);
  const isFirstLoad = useRef(true);

  // One-time fetch of full thread history on open
  // Parent's poll keeps statuses current after that
  const loadThread = useCallback(async () => {
    try {
      const raw = await serviceMessageGetVehicle(organizationId, convo.vehicleId);
      const msgs = (Array.isArray(raw) ? raw : (raw.messages ?? [])).map(
        normaliseMsg,
      );
      // Tell parent about the full history so it can merge into its state
      onLoadMore?.(convo.vehicleId, msgs);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [organizationId, convo.vehicleId]); // eslint-disable-line

  useEffect(() => {
    loadThread();
  }, [loadThread]);

  // Scroll to bottom on first load
  useEffect(() => {
    if (!loading && isFirstLoad.current && messages.length > 0) {
      setTimeout(
        () => bottomRef.current?.scrollIntoView({ behavior: "smooth" }),
        60,
      );
      isFirstLoad.current = false;
    }
  }, [loading, messages]);

  const handleSent = useCallback(
    (newMsg) => {
      const normalised = normaliseMsg(newMsg);
      onLoadMore?.(convo.vehicleId, [normalised]); // add to parent state
      setNewestId(normalised.messageCode || normalised._id);
      setTimeout(
        () => bottomRef.current?.scrollIntoView({ behavior: "smooth" }),
        80,
      );
    },
    [convo.vehicleId],
  ); // eslint-disable-line

  return (
    <div className="mdl-thread">
      <div className="mdl-thread-header">
        <button className="mdl-thread-btn-back" onClick={onBack}>
          <i className="bi bi-chevron-left" />
          <div className="mdl-thread-title">
            <i className="bi bi-truck" />
            <span>{convo.vehicleCode || convo.vehicleId}</span>
          </div>
        </button>
      </div>

      <div className="mdl-thread-body" ref={bodyRef}>
        {loading && messages.length === 0 && (
          <div className="mdl-state-msg">
            <div className="mdl-spinner" />
          </div>
        )}
        {error && (
          <div className="mdl-state-msg mdl-state-msg--error">
            <i className="bi bi-wifi-off" /> {error}
            <button onClick={loadThread}>Retry</button>
          </div>
        )}
        {!loading && !error && messages.length === 0 && (
          <div className="mdl-state-msg mdl-state-msg--empty">
            <i className="bi bi-chat-dots" />
            <span>No messages yet</span>
            <button
              className="mdl-empty-compose"
              onClick={() => onCompose(handleSent)}
            >
              Send first message
            </button>
          </div>
        )}

        {[...messages].reverse().map((msg) => (
          <MessageBubble
            key={msg._id || msg.messageCode}
            msg={msg}
            isNew={
              (msg._id && msg._id === newestId) ||
              (msg.messageCode && msg.messageCode === newestId)
            }
          />
        ))}

        <div ref={bottomRef} style={{ height: 1 }} />
      </div>

      <div className="mdl-compose-bar">
        <button
          className="mdl-compose-bar-btn"
          onClick={() => onCompose(handleSent)}
        >
          <i className="bi bi-pencil" /> NEW MESSAGE
        </button>
      </div>
    </div>
  );
};

// ── Root panel ────────────────────────────────────────────────────────────────
const FleetMessageList = ({ onClose, defaultVehicle, fleetList }) => {
  const [organizationId, setOrganizationId] = useState(null);

  const fetchOrganization = useCallback(async () => {
    try {
      const data = await serviceGetOrganizationDetails();

      setOrganizationId(data._id);
    } catch (err) {
      console.error("Failed to fetch admin profile:", err);
      toast.error(`Error: ${err?.message ?? "Unknown to load profile"}`);
      setOrganizationId(null);
    }
  }, []);

  useEffect(() => {
    fetchOrganization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOrganization]);

  const prevStatusesRef = useRef({});
  const flashTimersRef = useRef({});
  const activeConvoRef = useRef(null);

  const [conversations, setConversations] = useState([]);
  const [threadMessages, setThreadMessages] = useState({}); // { vehicleId: [msgs] }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeConvo, setActiveConvo] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [statusChanges, setStatusChanges] = useState({});

  const sentCallbackRef = useRef(null);

  const setActiveConvoSynced = useCallback((convo) => {
    activeConvoRef.current = convo;
    setActiveConvo(convo);
  }, []);

  // Merge messages for a vehicle into threadMessages state
  // Called by ThreadView on initial load and on send
  const handleThreadMessages = useCallback((vehicleId, incoming) => {
    setThreadMessages((prev) => {
      const existing = prev[vehicleId] ?? [];
      const serverKeys = new Set(incoming.map((m) => m.messageCode || m._id));
      // Keep optimistic messages not yet in incoming
      const kept = existing.filter(
        (m) => !serverKeys.has(m.messageCode || m._id),
      );
      // Merge: incoming (server truth) + any optimistic not yet confirmed
      const merged = [...incoming, ...kept];
      return { ...prev, [vehicleId]: merged };
    });
  }, []);

  const loadConversations = useCallback(async () => {
    if (!organizationId) {
      setError("Session expired");
      setLoading(false);
      return;
    }

    try {
      const raw = await serviceMessageGetAll(organizationId);
      const msgs = (Array.isArray(raw) ? raw : (raw.messages ?? [])).map(
        normaliseMsg,
      );
      const convos = groupIntoConversations(msgs, fleetList);
      setConversations(convos);
      setError(null);

      setThreadMessages((prev) => {
        const next = { ...prev };
        const byVehicle = new Map();

        for (const msg of msgs) {
          const vid = msg.vehicleId;
          if (!byVehicle.has(vid)) byVehicle.set(vid, []);
          byVehicle.get(vid).push(msg);
        }

        for (const [vid, serverMsgs] of byVehicle) {
          if (!next[vid]) continue; // thread not opened yet — skip

          // Build a status lookup from server: messageCode → status
          // Only update STATUS, don't replace full messages
          // This avoids overwriting full thread history with a partial list
          const statusByCode = new Map(
            serverMsgs.map((m) => [m.messageCode || m._id, m.messageStatus]),
          );

          next[vid] = next[vid].map((m) => {
            const key = m.messageCode || m._id;
            const newStatus = statusByCode.get(key);
            if (newStatus && newStatus !== m.messageStatus) {
              return { ...m, messageStatus: newStatus };
            }
            return m;
          });
        }

        return next;
      });

      // Detect status changes
      const newChanges = {};
      for (const convo of convos) {
        const vid = convo.vehicleId;
        const currentStatus = convo.lastMessage?.messageStatus;
        const prevStatus = prevStatusesRef.current[vid];

        if (
          prevStatus !== undefined &&
          prevStatus !== currentStatus &&
          DRIVER_STATUSES.has(currentStatus)
        ) {
          newChanges[vid] = currentStatus;
        }
        prevStatusesRef.current[vid] = currentStatus;
      }

      if (Object.keys(newChanges).length > 0) {
        setStatusChanges((prev) => ({ ...prev, ...newChanges }));
        for (const vid of Object.keys(newChanges)) {
          if (flashTimersRef.current[vid])
            clearTimeout(flashTimersRef.current[vid]);
          flashTimersRef.current[vid] = setTimeout(() => {
            setStatusChanges((prev) => {
              const next = { ...prev };
              delete next[vid];
              return next;
            });
            delete flashTimersRef.current[vid];
          }, FLASH_MS);
        }
      }

      // Auto-open defaultVehicle
      if (defaultVehicle && !activeConvoRef.current) {
        const vid = defaultVehicle.vehicleId;
        const match = convos.find((c) => c.vehicleId === vid);
        setActiveConvoSynced(
          match ?? {
            vehicleId: vid,
            vehicleCode:
              defaultVehicle.vehicleCode ?? getVehicleCode(vid, fleetList),
            lastMessage: null,
            pendingCount: 0,
          },
        );
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [organizationId, defaultVehicle, fleetList, setActiveConvoSynced]); // eslint-disable-line

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Single poll — always running
  useEffect(() => {
    const id = setInterval(loadConversations, POLL_MS);
    return () => clearInterval(id);
  }, [loadConversations]);

  // Cleanup flash timers on unmount
  useEffect(() => {
    return () => Object.values(flashTimersRef.current).forEach(clearTimeout);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleOpenCompose = useCallback((cb) => {
    sentCallbackRef.current = cb;
    setShowCompose(true);
  }, []);

  const handleConvoSent = useCallback(
    (newMsg) => {
      sentCallbackRef.current?.(newMsg);
      loadConversations();
      setShowCompose(false);
    },
    [loadConversations],
  );

  return (
    <>
      <div
        className="mdl-backdrop"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      />

      <div className="mdl-panel">
        <div className="mdl-panel-header">
          <i className="bi bi-chat-dots" />
          <span className="mdl-span-title">FLEET LINK</span>
          <button className="mdl-btn-close" onClick={onClose}>
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <div className="mdl-panel-content">
          {activeConvo ? (
            <ThreadView
              convo={activeConvo}
              organizationId={organizationId}
              messages={threadMessages[activeConvo.vehicleId] ?? []}
              onBack={() => {
                setActiveConvoSynced(null);
                setShowCompose(false);
              }}
              onCompose={handleOpenCompose}
              onLoadMore={handleThreadMessages}
            />
          ) : (
            <div className="mdl-convo-list">
              {loading && (
                <div className="mdl-state-msg">
                  <div className="mdl-spinner" />
                </div>
              )}
              {error && (
                <div className="mdl-state-msg mdl-state-msg--error">
                  <i className="bi bi-wifi-off" /> {error}
                  <button onClick={loadConversations}>Retry</button>
                </div>
              )}
              {!loading && !error && conversations.length === 0 && (
                <div className="mdl-state-msg mdl-state-msg--empty">
                  <i className="bi bi-chat-dots" />
                  <span>No conversations yet</span>
                </div>
              )}
              {conversations.map((convo) => (
                <ConversationItem
                  key={convo.vehicleId}
                  convo={convo}
                  isActive={activeConvo?.vehicleId === convo.vehicleId}
                  onClick={setActiveConvoSynced}
                  statusChange={statusChanges[convo.vehicleId]}
                />
              ))}
            </div>
          )}

          {showCompose && activeConvo && (
            <FleetMessagePrompt
              fleet={activeConvo}
              // organizationId={organizationId}
              onClose={() => setShowCompose(false)}
              onSent={handleConvoSent}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default FleetMessageList;
